import { lazy } from 'react';
import { Box, Text, Group, Progress, Badge, ScrollArea, useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { IconFolders } from '@tabler/icons-react';
import { WidgetProps, ProjectData } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// ProjectsProgress Widget Component
// ============================================================================

function ProjectsProgressWidget({ globalData, isCollapsed }: WidgetProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  if (isCollapsed) return null;

  const projects = globalData?.projects || [];

  // Filter to active projects and sort by progress
  const activeProjects = projects
    .filter((p) => p.status === 'in-progress' || p.status === 'planning')
    .sort((a, b) => b.progress - a.progress)
    .slice(0, 6);

  const getStatusColor = (status: ProjectData['status']) => {
    const colors: Record<ProjectData['status'], string> = {
      planning: 'gray',
      'in-progress': 'blue',
      'on-hold': 'yellow',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status];
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 75) return 'green';
    if (progress >= 50) return 'blue';
    if (progress >= 25) return 'yellow';
    return 'red';
  };

  if (activeProjects.length === 0) {
    return (
      <FadeIn style={{ height: '100%' }}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: theme.colors.gray[5],
          }}
        >
          <IconFolders size={32} stroke={1.5} />
          <Text size="sm" mt="xs" style={accessibilityStyles}>
            No active projects
          </Text>
        </Box>
      </FadeIn>
    );
  }

  return (
    <FadeIn style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        {activeProjects.map((project, index) => (
          <Box
            key={project.id}
            p="xs"
            mb="sm"
            style={{
              backgroundColor: colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
              borderRadius: theme.radius.md,
            }}
          >
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500} lineClamp={1} style={{ ...accessibilityStyles, flex: 1 }}>
                {project.name}
              </Text>
              <Badge size="xs" variant="light" color={getStatusColor(project.status)}>
                {project.status.replace('-', ' ')}
              </Badge>
            </Group>

            <Progress.Root size="lg" radius="xl">
              <Progress.Section
                value={project.progress}
                color={getProgressColor(project.progress)}
              >
                <Progress.Label>{project.progress}%</Progress.Label>
              </Progress.Section>
            </Progress.Root>

            <Group justify="space-between" mt="xs">
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                Due: {new Date(project.dueDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </Text>
              {project.memberIds && (
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {project.memberIds.length} members
                </Text>
              )}
            </Group>
          </Box>
        ))}
      </ScrollArea>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'projects-progress',
  name: 'Projects Progress',
  description: 'Overview of active project completion status with progress bars',
  icon: IconFolders,
  category: 'projects',
  component: lazy(() => Promise.resolve({ default: ProjectsProgressWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 12, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 4, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 4, rows: 4 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'tall', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 3, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 4, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 4, rows: 4 },
      },
      tall: {
        base: { cols: 12, rows: 7 },
        sm: { cols: 12, rows: 7 },
        md: { cols: 6, rows: 7 },
        lg: { cols: 4, rows: 7 },
        xl: { cols: 4, rows: 7 },
        tv: { cols: 4, rows: 6 },
      },
      wide: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 12, rows: 4 },
        lg: { cols: 8, rows: 4 },
        xl: { cols: 8, rows: 4 },
        tv: { cols: 8, rows: 4 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['projects'],
  },
  hasSettings: true,
});

export default ProjectsProgressWidget;
